import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import {
  useResearchJobsStore,
  type ResearchJob,
} from '../stores/researchJobsStore';
import { useWizardStore } from '../stores/wizardStore';
import { JOB_TIMEOUT_MS } from '../hooks/useResearchJobProgress';

/**
 * ResearchJobsNotifier
 * --------------------
 * Mounted once at the app root. Polls all running case-wizard research jobs
 * every 3 seconds. When a job finishes:
 *   - Writes the result back into the wizard store
 *   - Fires a sonner toast with a "Resume case" action
 *
 * Polling persists across page navigation because the store is in
 * localStorage — the user can leave the wizard, work elsewhere, and come back
 * to a ready case via the toast.
 *
 * IMPORTANT: The polling interval uses a ref to access the latest `jobs` so
 * that calling `updateJob` (which changes `jobs`) does NOT restart the
 * interval. Without this, every poll response would tear down and re-create
 * the interval, causing hundreds of back-to-back requests per second.
 */

const POLL_INTERVAL_MS = 3000;

export function ResearchJobsNotifier() {
  const navigate = useNavigate();
  const jobs = useResearchJobsStore((s) => s.jobs);
  const updateJob = useResearchJobsStore((s) => s.updateJob);
  const markNotified = useResearchJobsStore((s) => s.markNotified);
  const pruneOldJobs = useResearchJobsStore((s) => s.pruneOldJobs);
  const setIndustryData = useWizardStore((s) => s.setIndustryData);
  const setCompetitorsData = useWizardStore((s) => s.setCompetitorsData);

  // Always hold the latest snapshot of jobs for use inside the interval without
  // making the interval re-subscribe every time jobs changes.
  const jobsRef = useRef(jobs);
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  // Prevent concurrent in-flight polls for the same job.
  const inflightPolls = useRef<Set<string>>(new Set());

  useEffect(() => {
    pruneOldJobs();
  }, [pruneOldJobs]);

  // Stable polling interval — runs once on mount, never restarts due to store updates.
  useEffect(() => {
    const tick = async () => {
      const active = Object.values(jobsRef.current).filter(
        (j) => j.status === 'running',
      );
      for (const job of active) {
        // Hard timeout. Without this a job whose backend died mid-run stays
        // 'running' forever and this interval polls it for the lifetime of the
        // tab — the store is persisted, so that survives reloads too.
        if (Date.now() - job.createdAt > JOB_TIMEOUT_MS) {
          updateJob(job.jobId, {
            status: 'failed',
            error: `Timed out after ${Math.round(JOB_TIMEOUT_MS / 60000)} minutes without completing.`,
            message: 'Timed out',
          });
          continue;
        }
        if (inflightPolls.current.has(job.jobId)) continue;
        inflightPolls.current.add(job.jobId);
        try {
          const fresh = await api.get<{
            jobId: string;
            kind: ResearchJob['kind'];
            status: ResearchJob['status'];
            progress: number;
            message: string;
            result?: any;
            error?: string;
          }>(`/case-discovery/research-jobs/${job.jobId}`);

          updateJob(job.jobId, {
            status: fresh.status,
            progress: fresh.progress,
            message: fresh.message,
            result: fresh.result,
            error: fresh.error,
          });
        } catch (err: any) {
          // 404 means job expired or server restarted — fail it locally so the
          // user gets a notification instead of a forever-spinning state.
          const code = err?.response?.status;
          if (code === 404) {
            updateJob(job.jobId, {
              status: 'failed',
              error: 'Research job no longer available (it may have been cleared by a server restart).',
              message: 'Job not found',
            });
          }
        } finally {
          inflightPolls.current.delete(job.jobId);
        }
      }
    };

    // First tick fires immediately, then every POLL_INTERVAL_MS.
    void tick();
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable — uses jobsRef + updateJob (both stable refs)

  // Show toasts for newly-finished jobs and apply their results to the wizard store.
  // This effect IS allowed to depend on `jobs` because it doesn't call updateJob.
  useEffect(() => {
    for (const job of Object.values(jobs)) {
      if (job.notified) continue;
      if (job.status === 'completed') {
        try {
          if (job.kind === 'industry_research' && job.result?.industry) {
            const ind = job.result.industry;
            setIndustryData({
              ...ind,
              industryId: job.result.industryId ?? ind.industryId,
            });
          } else if (
            job.kind === 'competition_analysis' &&
            Array.isArray(job.result?.competitors)
          ) {
            setCompetitorsData(job.result.competitors);
          }
        } catch {
          // Don't block the toast if the apply step fails
        }

        const sourceLabel =
          job.result?.source === 'existing'
            ? 'reused from research library'
            : 'freshly generated';
        toast.success(`${job.label} is ready (${sourceLabel})`, {
          duration: 10000,
          action: job.resumePath
            ? {
                label: 'Resume case',
                onClick: () => navigate(job.resumePath!),
              }
            : undefined,
        });
        markNotified(job.jobId);
      } else if (job.status === 'failed') {
        toast.error(`${job.label} failed`, {
          description: job.error || 'Unknown error',
          duration: 8000,
        });
        markNotified(job.jobId);
      }
    }
  }, [jobs, markNotified, navigate, setCompetitorsData, setIndustryData]);

  return null;
}

export default ResearchJobsNotifier;
